import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {BackLink} from 'components/BackLink'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {useApSeoParentPlace} from 'hooks/useApSeoBreadcrumb'
import {useHotelSlug} from 'hooks/useHotelSlug'

import {useTrackingTeam} from '@vio/core/src/analytics/hooks/useTrackingTeam'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'

import {useSrpQueryString} from './helpers'

interface Props {
  /** Hide the "Go back to search" text link */
  hideText?: boolean
}

export const GoBackToSearchButton = ({hideText = false}: Props) => {
  const hotelSlug = useHotelSlug()
  const {changeLocation} = useUrlUpdater()
  const team = useTrackingTeam()
  const dispatch = useDispatch()
  const query = useSrpQueryString(hotelSlug)
  const seoParentPlace = useApSeoParentPlace()

  const {formatMessage} = useIntl()

  const handleBackToSearch = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsBackButton,
        action: Action.Clicked,
        team
      })
    )

    // In SEO SRP flow, navigate back to the parent breadcrumb path (e.g. city/region page) instead of the regular SRP
    if (seoParentPlace?.path) {
      setTimeout(() => {
        changeLocation(
          {},
          {
            basePath: seoParentPlace.path
          }
        )
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
      return
    }

    // timeout to allow the tracking event to be sent before navigating away
    setTimeout(() => {
      changeLocation(query)
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }, [changeLocation, query, dispatch, team, seoParentPlace])

  const goBackToSearchLabel = formatMessage({
    id: 'returnToSearch',
    defaultMessage: 'Go back to search'
  })

  return (
    <BackLink
      label={goBackToSearchLabel}
      onClick={handleBackToSearch}
      hideText={hideText}
    />
  )
}
